import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore'
import { db, storage } from '../../Firebase/firebase'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { 
  Spinner, 
  Button, 
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  Textarea,
  Select,
  SelectItem,
  Divider,
  Progress
} from '@heroui/react'
import useAuth from '../Helpers/useAuth'
import './estilos-diseño.css'
import swal from 'sweetalert'

// Importar componentes separados
import VistaPerfilPublico from './VistaPerfilPublico'
import EdicionPerfilPublico from './EdicionPerfilPublico'
import ModalEditarEnlace from './ModalEditarEnlace'
import RenderizarCuentaBancaria from './RenderizarCuentaBancaria'

// Importar constantes
import {
  iconosDisponibles,
  divisas,
  paletasColores,
  tipografiasDisponibles,
  tiposEnlaces,
  paisesBancarios,
  tiposCuenta
} from './ConstantesEnlaces'

// Función para renderizar iconos (FontAwesome o SVG personalizado)
const renderIcon = (iconoId, className = "") => {
  const icono = iconosDisponibles.find(i => i.id === iconoId)
  
  if (!icono) {
    // Si no se encuentra, buscar por clase (compatibilidad hacia atrás)
    const iconoPorClase = iconosDisponibles.find(i => i.clase === iconoId)
    if (iconoPorClase) {
      return <i className={`${iconoPorClase.clase} ${className}`}></i>
    }
    // Fallback para iconos antiguos (emojis y texto)
    return <span className={className}>{iconoId}</span>
  }
  
  if (icono.tipo === 'svg' && icono.componente) {
    const IconoSVG = icono.componente
    return <IconoSVG className={className} />
  } else {
    return <i className={`${icono.clase} ${className}`}></i>
  }
}

const PerfilPublico = ({ 
  usuarioData = null, 
  esPreview = false, 
  className = "",
  estiloMiniatura = false 
}) => {
  const { nombreUsuario } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  
  const [usuario, setUsuario] = useState(usuarioData)
  const [cargando, setCargando] = useState(!usuarioData)
  const [error, setError] = useState(null)
  const [esPerfilPropio, setEsPerfilPropio] = useState(false)

  // Estados para edición
  const [modoEdicion, setModoEdicion] = useState(false)
  const [guardandoCambios, setGuardandoCambios] = useState(false)
  const [subiendoFoto, setSubiendoFoto] = useState(false)
  const [progresoFoto, setProgresoFoto] = useState(0)
  
  // Estados para modales
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure()
  const { isOpen: isLinkModalOpen, onOpen: onLinkModalOpen, onClose: onLinkModalClose } = useDisclosure()
  const { isOpen: isPhotoModalOpen, onOpen: onPhotoModalOpen, onClose: onPhotoModalClose } = useDisclosure()
  const { isOpen: isTabModalOpen, onOpen: onTabModalOpen, onClose: onTabModalClose } = useDisclosure()

  // Estados para errores en modales
  const [errorModal, setErrorModal] = useState('')
  const [errorTabModal, setErrorTabModal] = useState('')
  const [errorFotoModal, setErrorFotoModal] = useState('')

  // Estados temporales solo para datos del perfil (no enlaces ni tabs)
  // Los enlaces y tabs se gestionan directamente en Firebase
  const [cambiosTemporales, setCambiosTemporales] = useState({
    nombre: '',
    bio: '',
    ubicacion: '',
    sitioWeb: '',
    tipografia: 'system',
    paletaColores: 'default',

    enlaces: [], // Se mantiene para compatibilidad pero se actualiza desde usuario
    tabs: []    // Se mantiene para compatibilidad pero se actualiza desde usuario
  })
  
  // Estados para formularios
  const [formDataPerfil, setFormDataPerfil] = useState({
    nombre: '',
    bio: '',
    ubicacion: '',
    sitioWeb: '',
    tipografia: 'system',
    paletaColores: 'default'
  })
  
  // Estados para gestión de tabs
  const [tabActual, setTabActual] = useState({
    id: '',
    nombre: '',
    icono: 'folder'
  })
  const [editandoTabIndex, setEditandoTabIndex] = useState(-1)
  const [tabSeleccionado, setTabSeleccionado] = useState('') // Para trackear el tab actualmente seleccionado
  
  // Estados para enlaces
  const [enlaceActual, setEnlaceActual] = useState({
    tipo: 'normal',
    titulo: '',
    url: '',
    descripcion: '',
    icono: 'link',
    precio: '',
    divisa: 'USD',
    imagen: '',
    tieneImagen: false,
    mostrarFotoGrande: false,
    esCuentaBancaria: false,
    tab: '', // Tab al que pertenece el enlace
    numeroWhatsApp: '',
    mensajePredefinido: '',
    codigoPais: '+52',
    esProducto: false,
    stock: '',
    categoria: '',
    disponible: true,
    // Campos específicos para ubicación
    nombreLugar: '',
    direccion: '',
    latitud: '',
    longitud: '',
    horarios: '',
    telefono: '',
    enlaceGoogleMaps: '',
    datosBancarios: {
      nombreBanco: '',
      numeroCuenta: '',
      tipoCuenta: 'ahorro',
      clabe: '',
      nombreTitular: '',
      identificacionTitular: '',
      paisBanco: 'MX'
    }
  })
  
  const [editandoEnlaceIndex, setEditandoEnlaceIndex] = useState(-1)

  // Función para copiar al portapapeles
  const copiarAlPortapapeles = async (texto, mensaje = 'Copiado al portapapeles') => {
    try {
      await navigator.clipboard.writeText(texto)
      swal('¡Copiado!', mensaje, 'success')
    } catch (error) {
      console.error('Error al copiar:', error)
      swal('Error', 'No se pudo copiar al portapapeles', 'error')
    }
  }

  // Función para cerrar modal de enlaces
  const cerrarModalEnlaces = () => {
    setErrorModal('') // Limpiar error
    onLinkModalClose()
  }

  // Función para inicializar datos del formulario (solo datos del perfil)
  const inicializarFormulario = () => {
    if (usuario) {
      const datosPerfil = {
        nombre: usuario.nombre || '',
        bio: usuario.bio || '',
        ubicacion: usuario.ubicacion || '',
        sitioWeb: usuario.sitioWeb || '',
        tipografia: usuario.tipografia || 'system',
        paletaColores: usuario.paletaColores || 'default',
        // Enlaces y tabs siempre se toman del estado actual del usuario
        enlaces: usuario.enlaces || [],
        tabs: usuario.tabs || []
      }
      
      setFormDataPerfil(datosPerfil)
      setCambiosTemporales(datosPerfil)
      
      // Inicializar el tab seleccionado si hay tabs disponibles
      if (usuario?.tabs && usuario.tabs.length > 0 && !tabSeleccionado) {
        setTabSeleccionado(usuario.tabs[0].id)
      }
    }
  }

  // Función para cancelar cambios (solo datos del perfil)
  const cancelarCambios = () => {
    setModoEdicion(false)
    // Reinicializar solo los datos del perfil, no enlaces ni tabs
    inicializarFormulario()
  }

  // Función para manejar cambios temporales
  const handleCambioTemporal = (campo, valor) => {
    setCambiosTemporales(prev => ({
      ...prev,
      [campo]: valor
    }))
  }

  // Función para manejar cambios en el formulario de enlace


  // Función para manejar cambios en el formulario de perfil
  const handleInputChangePerfil = (campo, valor) => {
    setFormDataPerfil(prev => ({
      ...prev,
      [campo]: valor
    }))
    handleCambioTemporal(campo, valor)
  }

  // Función para generar enlace de WhatsApp
  const generarEnlaceWhatsApp = (codigoPais, numeroWhatsApp, mensajePredefinido) => {
    if (!numeroWhatsApp) return ''
    
    const numeroLimpio = numeroWhatsApp.replace(/\D/g, '')
    const numeroCompleto = `${codigoPais.replace('+', '')}${numeroLimpio}`
    const mensajeCodificado = mensajePredefinido ? encodeURIComponent(mensajePredefinido) : ''
    
    return `https://wa.me/${numeroCompleto}${mensajeCodificado ? `?text=${mensajeCodificado}` : ''}`
  }

  // Función para generar enlace de Google Maps
  const generarEnlaceGoogleMaps = (direccion, latitud, longitud, enlaceGoogleMaps) => {
    // Si ya hay un enlace de Google Maps específico, usarlo
    if (enlaceGoogleMaps && enlaceGoogleMaps.trim()) {
      return enlaceGoogleMaps.trim()
    }
    
    if (!direccion && !latitud && !longitud) return ''
    
    // Si tenemos coordenadas, usarlas preferentemente
    if (latitud && longitud) {
      return `https://maps.google.com/maps?q=${latitud},${longitud}`
    }
    
    // Si solo tenemos dirección, usarla
    if (direccion) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(direccion)}`
    }
    
    return ''
  }

  // Función para obtener los estilos temporales
  const obtenerEstilosPerfilTemporales = () => {
    const paletaId = modoEdicion ? cambiosTemporales.paletaColores : (usuario?.paletaColores || 'default')
    const paletaActual = paletasColores.find(p => p.id === paletaId) || paletasColores[0]
    
    return {
      fondo: paletaActual.colores.fondo,
      fondoSecundario: paletaActual.colores.fondoSecundario,
      primario: paletaActual.colores.primario,
      secundario: paletaActual.colores.secundario,
      acento: paletaActual.colores.acento,
      texto: paletaActual.colores.texto,
      textoSecundario: paletaActual.colores.textoSecundario,
      borde: paletaActual.colores.borde
    }
  }

  // Función para obtener la tipografía temporal
  const obtenerTipografiaTemporales = () => {
    const tipografiaId = modoEdicion ? cambiosTemporales.tipografia : (usuario?.tipografia || 'system')
    const tipografia = tipografiasDisponibles.find(t => t.id === tipografiaId)
    return tipografia ? tipografia.familia : tipografiasDisponibles.find(t => t.id === 'system').familia
  }

  // Wrapper para RenderizarCuentaBancaria con estilos
  const RenderizarCuentaBancariaConEstilos = (props) => {
    return ( 
      <RenderizarCuentaBancaria
        {...props}
        estilosPerfil={obtenerEstilosPerfilTemporales()}
        estiloMiniatura={estiloMiniatura}
        copiarAlPortapapeles={copiarAlPortapapeles}
      />
    )
  }



  // Resto de funciones implementadas de forma simplificada...
  const abrirModalNuevoEnlace = () => {
    setEnlaceActual(null) // El modal manejará su propio estado
    setEditandoEnlaceIndex(-1)
    setErrorModal('') // Limpiar error
    onLinkModalOpen()
  }

  const abrirModalEdicionEnlace = (enlace, index) => {
    setEnlaceActual(enlace) // El modal manejará su propio estado
    setEditandoEnlaceIndex(index)
    setErrorModal('') // Limpiar error
    onLinkModalOpen()
  }

  // Función para guardar tab directamente en Firebase
  const guardarTab = async () => {
    // Limpiar error previo
    setErrorTabModal('')
    
    if (!tabActual.nombre.trim()) {
      setErrorTabModal('El nombre del tab es requerido')
      return
    }

    // Asegurar que el tab tenga todos los valores necesarios
    const tabParaGuardar = {
      id: tabActual.id || tabActual.nombre.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '-' + Date.now(),
      nombre: tabActual.nombre.trim(),
      icono: tabActual.icono || 'folder'
    }

    setGuardandoCambios(true)
    try {
      let nuevosTabs = [...(usuario.tabs || [])]

      if (editandoTabIndex === -1) {
        // Nuevo tab
        nuevosTabs.push(tabParaGuardar)
      } else {
        // Editar tab existente
        nuevosTabs[editandoTabIndex] = tabParaGuardar
      }

      // Guardar en Firebase inmediatamente
      await updateDoc(doc(db, "usuarios", user.uid), {
        tabs: nuevosTabs
      })

      // Actualizar estado local
      setUsuario(prev => ({
        ...prev,
        tabs: nuevosTabs
      }))

      // Actualizar cambios temporales también para sincronizar
      handleCambioTemporal('tabs', nuevosTabs)

      swal('¡Éxito!', 'Tab guardado correctamente', 'success')
      setErrorTabModal('')
      onTabModalClose()
    } catch (error) {
      console.error('Error al guardar tab:', error)
      setErrorTabModal('No se pudo guardar el tab. Inténtalo de nuevo.')
    } finally {
      setGuardandoCambios(false)
    }
  }



  // Función para subir imagen si es necesario
  const subirImagenSiEsNecesario = async (imagenData) => {
    if (!imagenData || imagenData.startsWith('http')) {
      // Si no hay imagen o ya es una URL, no hacer nada
      return imagenData
    }

    if (imagenData.startsWith('data:')) {
      // Es un data URL, necesitamos subirlo
      try {
        // Convertir data URL a blob
        const response = await fetch(imagenData)
        const blob = await response.blob()
        
        // Crear referencia en Storage
        const nombreArchivo = `enlace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const storageRef = ref(storage, `usuarios/${user.uid}/enlaces/${nombreArchivo}`)
        
        // Subir imagen
        const uploadTask = uploadBytesResumable(storageRef, blob)
        
        return new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              // Progreso de subida (podrías usar esto para mostrar progreso)
            },
            (error) => {
              console.error('Error al subir imagen:', error)
              reject(error)
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
              console.log('Imagen subida exitosamente:', downloadURL)
              resolve(downloadURL)
            }
          )
        })
      } catch (error) {
        console.error('Error al procesar imagen:', error)
        return '' // Devolver string vacío si falla
      }
    }

    return '' // Fallback
  }

  // Función para guardar enlace directamente en Firebase
  const guardarEnlace = async (enlaceData, index) => {
    if (!user?.uid) return

    // Limpiar error previo
    setErrorModal('')
    setGuardandoCambios(true)
    
    try {
      // Validar campos requeridos
      if (!enlaceData.titulo.trim()) {
        setErrorModal('El título es requerido')
        return
      }

      let urlFinal = enlaceData.url

      // Validar según el tipo de enlace
      if (enlaceData.tipo === 'cuenta_bancaria') {
        // Validar datos bancarios
        if (!enlaceData.datosBancarios.nombreBanco.trim() || !enlaceData.datosBancarios.numeroCuenta.trim()) {
          setErrorModal('El nombre del banco y número de cuenta son requeridos')
          return
        }
      } else if (enlaceData.tipo === 'whatsapp') {
        // Validar WhatsApp
        if (!enlaceData.numeroWhatsApp.trim()) {
          setErrorModal('El número de WhatsApp es requerido')
          return
        }
        
        // Generar enlace de WhatsApp
        urlFinal = generarEnlaceWhatsApp(
          enlaceData.codigoPais, 
          enlaceData.numeroWhatsApp, 
          enlaceData.mensajePredefinido
        )
      } else if (enlaceData.tipo === 'producto') {
        // Validar producto
        if (!enlaceData.url.trim()) {
          setErrorModal('La URL del producto es requerida')
          return
        }
        
        if (!enlaceData.url.match(/^https?:\/\/.+/)) {
          setErrorModal('La URL debe comenzar con http:// o https://')
          return
        }
        
        if (!enlaceData.precio) {
          setErrorModal('El precio es requerido para productos')
          return
        }
      } else if (enlaceData.tipo === 'ubicacion') {
        // Validar ubicación - debe tener enlace de Google Maps
        if (!enlaceData.enlaceGoogleMaps.trim()) {
          setErrorModal('Debes proporcionar un enlace de Google Maps')
          return
        }
        
        // Validar que sea un enlace válido de Google Maps
        if (!enlaceData.enlaceGoogleMaps.includes('maps.google') && 
            !enlaceData.enlaceGoogleMaps.includes('goo.gl/maps') &&
            !enlaceData.enlaceGoogleMaps.includes('google.com/maps')) {
          setErrorModal('El enlace debe ser de Google Maps')
          return
        }
        
        // Asegurar que tenga al menos un nombre de lugar
        if (!enlaceData.nombreLugar.trim()) {
          enlaceData.nombreLugar = 'Ubicación'
        }
        
        // Generar enlace de Google Maps
        urlFinal = enlaceData.enlaceGoogleMaps
      } else {
        // Validar URL para enlaces normales
        if (!enlaceData.url.trim()) {
          setErrorModal('La URL es requerida')
          return
        }
        
        if (!enlaceData.url.match(/^https?:\/\/.+/)) {
          setErrorModal('La URL debe comenzar con http:// o https://')
          return
        }
      }

      // Subir imagen si es necesario
      const imagenURL = await subirImagenSiEsNecesario(enlaceData.imagen)

      // Limpiar y preparar datos para Firebase (sin valores undefined o problemáticos)
      const nuevoEnlace = {
        id: index === -1 ? `enlace_${Date.now()}` : (usuario.enlaces && usuario.enlaces[index])?.id || `enlace_${Date.now()}`,
        tipo: enlaceData.tipo || 'normal',
        titulo: (enlaceData.titulo || '').trim(),
        url: urlFinal || '',
        descripcion: (enlaceData.descripcion || '').trim(),
        icono: enlaceData.icono || 'link',
        precio: enlaceData.precio || '',
        divisa: enlaceData.divisa || 'USD',
        descuento: enlaceData.descuento || '', // Agregar campo de descuento
        // Para imagen, usar la URL subida o vacía
        imagen: imagenURL || '',
        tieneImagen: Boolean(imagenURL),
        mostrarFotoGrande: Boolean(enlaceData.mostrarFotoGrande),
        esCuentaBancaria: enlaceData.tipo === 'cuenta_bancaria',
        tab: enlaceData.tab || '', // Incluir el tab del enlace
        // Campos específicos para WhatsApp
        numeroWhatsApp: enlaceData.numeroWhatsApp || '',
        mensajePredefinido: enlaceData.mensajePredefinido || '',
        codigoPais: enlaceData.codigoPais || '+52',
        // Campos específicos para productos
        esProducto: enlaceData.tipo === 'producto',
        stock: enlaceData.stock || '',
        categoria: enlaceData.categoria || '',
        disponible: enlaceData.disponible !== false, // Default true
        // Campos específicos para ubicación
        nombreLugar: enlaceData.nombreLugar || '',
        direccion: enlaceData.direccion || '',
        latitud: enlaceData.latitud || '',
        longitud: enlaceData.longitud || '',
        horarios: enlaceData.horarios || '',
        telefono: enlaceData.telefono || '',
        enlaceGoogleMaps: enlaceData.enlaceGoogleMaps || '',
        // Datos bancarios solo si es cuenta bancaria y están completos
        ...(enlaceData.tipo === 'cuenta_bancaria' && enlaceData.datosBancarios ? {
          datosBancarios: {
            nombreBanco: enlaceData.datosBancarios.nombreBanco || '',
            paisBanco: enlaceData.datosBancarios.paisBanco || 'MX',
            numeroCuenta: enlaceData.datosBancarios.numeroCuenta || '',
            tipoCuenta: enlaceData.datosBancarios.tipoCuenta || 'corriente',
            clabe: enlaceData.datosBancarios.clabe || '',
            nombreTitular: enlaceData.datosBancarios.nombreTitular || '',
            identificacionTitular: enlaceData.datosBancarios.identificacionTitular || ''
          }
        } : {})
      }

      // Actualizar directamente en Firebase
      let nuevosEnlaces = [...(usuario.enlaces || [])]
      
      if (index === -1) {
        // Nuevo enlace
        nuevosEnlaces.push(nuevoEnlace)
      } else {
        // Editar enlace existente
        nuevosEnlaces[index] = nuevoEnlace
      }

      // Guardar en Firebase inmediatamente
      await updateDoc(doc(db, "usuarios", user.uid), {
        enlaces: nuevosEnlaces
      })

      // Actualizar estado local
      setUsuario(prev => ({
        ...prev,
        enlaces: nuevosEnlaces
      }))

      // Actualizar cambios temporales también para sincronizar
      handleCambioTemporal('enlaces', nuevosEnlaces)

      swal('¡Éxito!', 'Enlace guardado correctamente', 'success')
      setErrorModal('') // Limpiar error al guardar exitosamente
      cerrarModalEnlaces() // Cerrar modal al guardar exitosamente
    } catch (error) {
      console.error('Error al guardar enlace:', error)
      setErrorModal('No se pudo guardar el enlace. Inténtalo de nuevo.')
      throw error // Re-lanzar para que el modal maneje el error
    } finally {
      setGuardandoCambios(false)
    }
  }

  // Función para eliminar enlace directamente de Firebase
  const eliminarEnlace = async (index) => {
    if (!user?.uid) return

    try {
      const result = await swal({
        title: "¿Estás seguro?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      })

      if (result) {
        const nuevosEnlaces = [...(usuario.enlaces || [])]
        nuevosEnlaces.splice(index, 1)

        // Guardar en Firebase inmediatamente
        await updateDoc(doc(db, "usuarios", user.uid), {
          enlaces: nuevosEnlaces
        })

        // Actualizar estado local
        setUsuario(prev => ({
          ...prev,
          enlaces: nuevosEnlaces
        }))

        // Actualizar cambios temporales también para sincronizar
        handleCambioTemporal('enlaces', nuevosEnlaces)

        //swal('¡Eliminado!', 'Enlace eliminado correctamente', 'success')
      }
    } catch (error) {
      console.error('Error al eliminar enlace:', error)
      swal('Error', 'No se pudo eliminar el enlace', 'error')
    }
  }

  // Función para mover enlace y guardar directamente en Firebase
  const moverEnlace = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return
    if (!user?.uid) return

    try {
      const nuevosEnlaces = [...(usuario.enlaces || [])]
      const [elementoMovido] = nuevosEnlaces.splice(fromIndex, 1)
      nuevosEnlaces.splice(toIndex, 0, elementoMovido)

      // Guardar en Firebase inmediatamente
      await updateDoc(doc(db, "usuarios", user.uid), {
        enlaces: nuevosEnlaces
      })

      // Actualizar estado local
      setUsuario(prev => ({
        ...prev,
        enlaces: nuevosEnlaces
      }))

      // Actualizar cambios temporales también para sincronizar
      handleCambioTemporal('enlaces', nuevosEnlaces)
    } catch (error) {
      console.error('Error al mover enlace:', error)
    }
  }

  // Función para abrir modal de nuevo tab
  const abrirModalNuevoTab = () => {
    setTabActual({
      id: '',
      nombre: '',
      icono: 'folder'
    })
    setEditandoTabIndex(-1)
    setErrorTabModal('') // Limpiar error
    onTabModalOpen()
  }

  // Función para abrir modal de editar tab
  const abrirModalEditarTab = (tab, index) => {
    setTabActual({
      id: tab.id || '',
      nombre: tab.nombre || '',
      icono: tab.icono || 'folder'
    })
    setEditandoTabIndex(index)
    setErrorTabModal('') // Limpiar error
    onTabModalOpen()
  }

  // Función para eliminar tab directamente de Firebase
  const eliminarTab = async (index) => {
    if (!user?.uid) return

    try {
      const result = await swal({
        title: "¿Estás seguro?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      })

      if (result) {
        const nuevosTabs = [...(usuario.tabs || [])]
        nuevosTabs.splice(index, 1)

        // Guardar en Firebase inmediatamente
        await updateDoc(doc(db, "usuarios", user.uid), {
          tabs: nuevosTabs
        })

        // Actualizar estado local
        setUsuario(prev => ({
          ...prev,
          tabs: nuevosTabs
        }))

        // Actualizar cambios temporales también para sincronizar
        handleCambioTemporal('tabs', nuevosTabs)
        
        swal('¡Eliminado!', 'Tab eliminado correctamente', 'success')
      }
    } catch (error) {
      console.error('Error al eliminar tab:', error)
      swal('Error', 'No se pudo eliminar el tab', 'error')
    }
  }

  // Función para mover tab y guardar directamente en Firebase
  const moverTab = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return
    if (!user?.uid) return

    try {
      const nuevosTabs = [...(usuario.tabs || [])]
      const [elementoMovido] = nuevosTabs.splice(fromIndex, 1)
      nuevosTabs.splice(toIndex, 0, elementoMovido)

      // Guardar en Firebase inmediatamente
      await updateDoc(doc(db, "usuarios", user.uid), {
        tabs: nuevosTabs
      })

      // Actualizar estado local
      setUsuario(prev => ({
        ...prev,
        tabs: nuevosTabs
      }))

      // Actualizar cambios temporales también para sincronizar
      handleCambioTemporal('tabs', nuevosTabs)
    } catch (error) {
      console.error('Error al mover tab:', error)
    }
  }



  // Función para subir foto de perfil
  const subirFotoPerfil = async (file) => {
    if (!file || !user?.uid) return null

    setSubiendoFoto(true)
    setProgresoFoto(0)
    
    try {
      const storageRef = ref(storage, `perfiles/${user.uid}/${Date.now()}_${file.name}`)
      const uploadTask = uploadBytesResumable(storageRef, file)
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            setProgresoFoto(Math.round(progress))
          },
          (error) => {
            console.error('Error al subir foto:', error)
            reject(error)
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
            resolve(downloadURL)
          }
        )
      })
    } catch (error) {
      console.error('Error al subir foto:', error)
      throw error
    } finally {
      setSubiendoFoto(false)
      setProgresoFoto(0)
    }
  }

  // Función para cambiar foto de perfil
  const handleCambiarFotoPerfil = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Limpiar error previo
    setErrorFotoModal('')

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setErrorFotoModal('Solo se permiten archivos de imagen')
      return
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorFotoModal('La imagen debe ser menor a 5MB')
      return
    }

    try {
      const nuevaURLFoto = await subirFotoPerfil(file)
      
      // Actualizar en Firebase
      await updateDoc(doc(db, "usuarios", user.uid), {
        fotoPerfil: nuevaURLFoto
      })

      // Actualizar estado local
      setUsuario(prev => ({
        ...prev,
        fotoPerfil: nuevaURLFoto
      }))

      swal('¡Éxito!', 'Foto de perfil actualizada correctamente', 'success')
      setErrorFotoModal('')
      onPhotoModalClose()
    } catch (error) {
      console.error('Error al cambiar foto:', error)
      setErrorFotoModal('No se pudo cambiar la foto de perfil. Inténtalo de nuevo.')
    }
  }

  // Función para guardar cambios del perfil (solo datos personales, no enlaces ni tabs)
  const guardarCambiosPerfil = async () => {
    if (!user?.uid) return

    setGuardandoCambios(true)
    
    try {
      // Validar campos requeridos
      if (!cambiosTemporales.nombre || !cambiosTemporales.nombre.trim()) {
        swal('Error', 'El nombre es requerido', 'error')
        return
      }

      // Preparar datos para Firebase - SOLO datos del perfil
      const datosPerfilSolamente = {
        nombre: (cambiosTemporales.nombre || '').trim(),
        bio: (cambiosTemporales.bio || '').trim(),
        ubicacion: (cambiosTemporales.ubicacion || '').trim(),
        sitioWeb: (cambiosTemporales.sitioWeb || '').trim(),
        tipografia: cambiosTemporales.tipografia || 'system',
        paletaColores: cambiosTemporales.paletaColores || 'default'
      }
      
      console.log('Datos del perfil a guardar:', datosPerfilSolamente)
      
      // Actualizar en Firebase solo los datos del perfil
      await updateDoc(doc(db, "usuarios", user.uid), datosPerfilSolamente)

      // Actualizar estado local con los datos del perfil
      setUsuario(prev => ({
        ...prev,
        ...datosPerfilSolamente
      }))

      // Salir del modo edición
      setModoEdicion(false)
      swal('¡Éxito!', 'Perfil actualizado correctamente', 'success')
    } catch (error) {
      console.error('Error al guardar perfil:', error)
      swal('Error', 'No se pudo guardar el perfil', 'error')
    } finally {
      setGuardandoCambios(false)
    }
  }

  // Efecto para cargar usuario
  useEffect(() => {
    if (usuarioData) {
      setUsuario(usuarioData)
      setCargando(false)
      if (isAuthenticated && user?.uid && usuarioData.id === user.uid) {
        setEsPerfilPropio(true)
      }
      return
    }

    const obtenerUsuario = async () => {
      try {
        setCargando(true)
        setError(null)
        
        const q = query(
          collection(db, "usuarios"), 
          where("nombreUsuario", "==", nombreUsuario.toLowerCase())
        )
        
        const querySnapshot = await getDocs(q)
        
        if (querySnapshot.empty) {
          setError("Usuario no encontrado")
          setUsuario(null)
        } else {
          const userData = querySnapshot.docs[0].data()
          const usuarioCompleto = {
            id: querySnapshot.docs[0].id,
            ...userData
          }
          setUsuario(usuarioCompleto)
          
          if (isAuthenticated && user?.uid && (user.uid === querySnapshot.docs[0].id)) {
            setEsPerfilPropio(true)
          } else {
            setEsPerfilPropio(false)
          }
        }
      } catch (error) {
        console.error('Error al obtener usuario:', error)
        setError("Error al cargar el perfil")
      } finally {
        setCargando(false)
      }
    }

    if (nombreUsuario) {
      obtenerUsuario()
    }
  }, [nombreUsuario, usuarioData, isAuthenticated, user])

  // Efecto para inicializar formulario cuando se carga el usuario
  useEffect(() => {
    if (usuario) {
      inicializarFormulario()
      if (isAuthenticated && user?.uid && (user.uid === usuario.id)) {
        setEsPerfilPropio(true)
      } else {
        setEsPerfilPropio(false)
      }
    }
  }, [user, isAuthenticated, usuario])

  // Efecto para inicializar el tab seleccionado cuando se cargan los datos del usuario
  useEffect(() => {
    if (usuario && !tabSeleccionado) {
      // Inicializar con el primer tab disponible del usuario (estado actual)
      const tabsDisponibles = usuario.tabs || []
      if (tabsDisponibles.length > 0) {
        setTabSeleccionado(tabsDisponibles[0].id)
      }
    }
  }, [usuario?.tabs, tabSeleccionado])

  if (cargando) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" color="primary" />
          <p className="text-gray-600">Cargando perfil...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="max-w-md text-center">
          <div className="mb-4">
            <span className="text-6xl">😔</span>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-gray-800">{error}</h1>
          <p className="text-gray-600 mb-4">
            El perfil que buscas no existe o no está disponible.
          </p>
          <Button 
            color="primary" 
            onPress={() => window.location.href = '/'}
          >
            Ir al inicio
          </Button>
        </div>
      </div>
    )
  }

  if (!usuario) {
    return null
  }

  const estilosPerfil = obtenerEstilosPerfilTemporales()

  return (
    <div 
      className={`${estiloMiniatura ? 'p-1 rounded-lg' : 'min-h-screen py-2'} ${className}`}
      style={{
        background: estilosPerfil.fondo,
        fontFamily: obtenerTipografiaTemporales()
      }}
    >
      {/* Barra de edición para el perfil propio */}
      {esPerfilPropio && !esPreview && !estiloMiniatura && (
        <div 
          className="text-white py-2 px-4 mb-4 shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${estilosPerfil.primario}, ${estilosPerfil.secundario})`
          }}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-x-4 flex-wrap">
              <span className="text-xl font-bold">✨ Este es tu Wachalo.co</span>
              <span className="text-blue-100 text-sm bg-blue-800 bg-opacity-50 px-3 py-1 rounded-full">
                Solo tú puedes ver esta barra
              </span>
            </div>
            <div className="flex items-center gap-3">
              {!modoEdicion ? (
                <>
                  <Button
                    color="warning"
                    variant="solid"
                    size="sm"
                    onPress={() => {
                      inicializarFormulario()
                      setModoEdicion(true)
                    }}
                    className="font-semibold"
                    startContent={<i className="fas fa-edit"></i>}
                  >
                    Editar Perfil
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    color="success"
                    variant="solid"
                    size="sm"
                    onPress={guardarCambiosPerfil}
                    isLoading={guardandoCambios}
                    className="font-semibold"
                    startContent={<i className="fas fa-save"></i>}
                  >
                    Guardar Cambios
                  </Button>
                  <Button
                    color="danger"
                    variant="solid"
                    size="sm"
                    onPress={cancelarCambios}
                    className="font-semibold"
                    startContent={<i className="fas fa-times"></i>}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      {modoEdicion ? (
        <EdicionPerfilPublico
          usuario={usuario}
          cambiosTemporales={{
            ...cambiosTemporales,
            // Para enlaces y tabs, usar siempre los datos actualizados del usuario
            enlaces: usuario.enlaces || [],
            tabs: usuario.tabs || []
          }}
          estiloMiniatura={estiloMiniatura}
          estilosPerfil={estilosPerfil}
          tipografia={obtenerTipografiaTemporales()}
          handleCambioTemporal={handleCambioTemporal}
          abrirModalNuevoEnlace={abrirModalNuevoEnlace}
          abrirModalNuevoTab={abrirModalNuevoTab}
          abrirModalEditarTab={abrirModalEditarTab}
          eliminarTab={eliminarTab}
          moverTab={moverTab}
          abrirModalEdicionEnlace={abrirModalEdicionEnlace}
          eliminarEnlace={eliminarEnlace}
          moverEnlace={moverEnlace}
          onPhotoModalOpen={() => {
            setErrorFotoModal('') // Limpiar error
            onPhotoModalOpen()
          }}
          RenderizarCuentaBancaria={RenderizarCuentaBancariaConEstilos}
          renderIcon={renderIcon}
        />
      ) : (
        <VistaPerfilPublico
          usuario={modoEdicion ? { 
            ...usuario, 
            nombre: cambiosTemporales.nombre || usuario.nombre,
            bio: cambiosTemporales.bio || usuario.bio,
            ubicacion: cambiosTemporales.ubicacion || usuario.ubicacion,
            sitioWeb: cambiosTemporales.sitioWeb || usuario.sitioWeb,
            tipografia: cambiosTemporales.tipografia || usuario.tipografia,
            paletaColores: cambiosTemporales.paletaColores || usuario.paletaColores
          } : usuario}
          esPerfilPropio={esPerfilPropio}
          estiloMiniatura={estiloMiniatura}
          estilosPerfil={estilosPerfil}
          tipografia={obtenerTipografiaTemporales()}
          abrirModalNuevoEnlace={abrirModalNuevoEnlace}
          RenderizarCuentaBancaria={RenderizarCuentaBancariaConEstilos}
          renderIcon={renderIcon}
        />
      )}

      {/* Modal de edición de enlace */}
      <ModalEditarEnlace
        isOpen={isLinkModalOpen}
        onClose={cerrarModalEnlaces}
        enlaceInicial={enlaceActual}
        editandoEnlaceIndex={editandoEnlaceIndex}
        onGuardar={guardarEnlace}
        tabs={usuario.tabs || []}
        tabSeleccionado={tabSeleccionado}
        generarEnlaceWhatsApp={generarEnlaceWhatsApp}
        errorMessage={errorModal}
        guardando={guardandoCambios}
      />

      {/* Modal de cambio de foto */}
      <Modal 
        isOpen={isPhotoModalOpen} 
        onClose={() => {
          setErrorFotoModal('') // Limpiar error al cerrar
          onPhotoModalClose()
        }}
        size="md"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-2">
              <i className="fas fa-camera text-primary"></i>
              <span>Cambiar Foto de Perfil</span>
            </div>
          </ModalHeader>
          <ModalBody>
            {/* Mensaje de error */}
            {errorFotoModal && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{errorFotoModal}</span>
                </div>
              </div>
            )}
            
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Selecciona una nueva foto para tu perfil
              </p>
              
              {subiendoFoto && (
                <div className="mb-4">
                  <Progress 
                    value={progresoFoto} 
                    className="mb-2"
                    color="primary"
                  />
                  <p className="text-sm text-gray-600">
                    Subiendo foto... {progresoFoto}%
                  </p>
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleCambiarFotoPerfil}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={subiendoFoto}
              />
              
              <p className="text-xs text-gray-500 mt-2">
                Formatos permitidos: JPG, PNG, GIF (máximo 5MB)
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="danger" 
              variant="light" 
              onPress={() => {
                setErrorFotoModal('') // Limpiar error al cancelar
                onPhotoModalClose()
              }}
              disabled={subiendoFoto}
            >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de gestión de tabs */}
      <Modal 
        isOpen={isTabModalOpen} 
        onClose={() => {
          setErrorTabModal('') // Limpiar error al cerrar
          onTabModalClose()
        }} 
        size="md"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-2">
              {renderIcon('folder', 'text-primary')}
              <span>{editandoTabIndex === -1 ? 'Crear Nuevo Tab' : 'Editar Tab'}</span>
            </div>
          </ModalHeader>
          <ModalBody>
            {/* Mensaje de error */}
            {errorTabModal && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{errorTabModal}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <Input
                label="Nombre del Tab"
                placeholder="Ej: Redes Sociales, Productos, etc."
                value={tabActual.nombre}
                onChange={(e) => setTabActual(prev => ({ ...prev, nombre: e.target.value }))}
                isRequired
                startContent={<i className="fas fa-tag text-gray-400"></i>}
              />
              
              <Select
                label="Icono"
                value={tabActual.icono}
                onChange={(e) => setTabActual(prev => ({ ...prev, icono: e.target.value }))}
                startContent={renderIcon(tabActual.icono, 'text-gray-400')}
              >
                {/* Iconos organizativos */}
                <SelectItem key="folder" value="folder" textValue="Carpeta">
                  <div className="flex items-center gap-2">
                    {renderIcon('folder')}
                    <span>Carpeta</span>
                  </div>
                </SelectItem>
                <SelectItem key="briefcase" value="briefcase" textValue="Trabajo">
                  <div className="flex items-center gap-2">
                    {renderIcon('briefcase')}
                    <span>Trabajo</span>
                  </div>
                </SelectItem>
                <SelectItem key="heart" value="heart" textValue="Favoritos">
                  <div className="flex items-center gap-2">
                    {renderIcon('heart')}
                    <span>Favoritos</span>
                  </div>
                </SelectItem>
                <SelectItem key="star" value="star" textValue="Destacados">
                  <div className="flex items-center gap-2">
                    {renderIcon('star')}
                    <span>Destacados</span>
                  </div>
                </SelectItem>
                <SelectItem key="home" value="home" textValue="Inicio">
                  <div className="flex items-center gap-2">
                    {renderIcon('home')}
                    <span>Inicio</span>
                  </div>
                </SelectItem>
                
                {/* Iconos de categorías */}
                <SelectItem key="shopping" value="shopping" textValue="Tienda">
                  <div className="flex items-center gap-2">
                    {renderIcon('shopping')}
                    <span>Tienda</span>
                  </div>
                </SelectItem>
                <SelectItem key="music" value="music" textValue="Música">
                  <div className="flex items-center gap-2">
                    {renderIcon('music')}
                    <span>Música</span>
                  </div>
                </SelectItem>
                <SelectItem key="camera" value="camera" textValue="Fotografía">
                  <div className="flex items-center gap-2">
                    {renderIcon('camera')}
                    <span>Fotografía</span>
                  </div>
                </SelectItem>
                <SelectItem key="film" value="film" textValue="Video">
                  <div className="flex items-center gap-2">
                    {renderIcon('film')}
                    <span>Video</span>
                  </div>
                </SelectItem>
                <SelectItem key="gamepad" value="gamepad" textValue="Juegos">
                  <div className="flex items-center gap-2">
                    {renderIcon('gamepad')}
                    <span>Juegos</span>
                  </div>
                </SelectItem>
                <SelectItem key="book" value="book" textValue="Blog">
                  <div className="flex items-center gap-2">
                    {renderIcon('book')}
                    <span>Blog</span>
                  </div>
                </SelectItem>
                
                {/* Iconos de comunicación */}
                <SelectItem key="email" value="email" textValue="Email">
                  <div className="flex items-center gap-2">
                    {renderIcon('email')}
                    <span>Email</span>
                  </div>
                </SelectItem>
                <SelectItem key="phone" value="phone" textValue="Teléfono">
                  <div className="flex items-center gap-2">
                    {renderIcon('phone')}
                    <span>Teléfono</span>
                  </div>
                </SelectItem>
                <SelectItem key="globe" value="globe" textValue="Sitio Web">
                  <div className="flex items-center gap-2">
                    {renderIcon('globe')}
                    <span>Sitio Web</span>
                  </div>
                </SelectItem>
                <SelectItem key="link" value="link" textValue="Enlaces">
                  <div className="flex items-center gap-2">
                    {renderIcon('link')}
                    <span>Enlaces</span>
                  </div>
                </SelectItem>
                
                {/* Iconos bancarios */}
                <SelectItem key="bank" value="bank" textValue="Banco">
                  <div className="flex items-center gap-2">
                    {renderIcon('bank')}
                    <span>Banco</span>
                  </div>
                </SelectItem>
                <SelectItem key="wallet" value="wallet" textValue="Billetera">
                  <div className="flex items-center gap-2">
                    {renderIcon('wallet')}
                    <span>Billetera</span>
                  </div>
                </SelectItem>
                
                {/* Redes sociales principales */}
                <SelectItem key="instagram" value="instagram" textValue="Instagram">
                  <div className="flex items-center gap-2">
                    {renderIcon('instagram')}
                    <span>Instagram</span>
                  </div>
                </SelectItem>
                <SelectItem key="facebook" value="facebook" textValue="Facebook">
                  <div className="flex items-center gap-2">
                    {renderIcon('facebook')}
                    <span>Facebook</span>
                  </div>
                </SelectItem>
                <SelectItem key="twitter" value="twitter" textValue="Twitter">
                  <div className="flex items-center gap-2">
                    {renderIcon('twitter')}
                    <span>Twitter</span>
                  </div>
                </SelectItem>
                <SelectItem key="youtube" value="youtube" textValue="YouTube">
                  <div className="flex items-center gap-2">
                    {renderIcon('youtube')}
                    <span>YouTube</span>
                  </div>
                </SelectItem>
                <SelectItem key="linkedin" value="linkedin" textValue="LinkedIn">
                  <div className="flex items-center gap-2">
                    {renderIcon('linkedin')}
                    <span>LinkedIn</span>
                  </div>
                </SelectItem>
                <SelectItem key="tiktok" value="tiktok" textValue="TikTok">
                  <div className="flex items-center gap-2">
                    {renderIcon('tiktok')}
                    <span>TikTok</span>
                  </div>
                </SelectItem>
                <SelectItem key="whatsapp" value="whatsapp" textValue="WhatsApp">
                  <div className="flex items-center gap-2">
                    {renderIcon('whatsapp')}
                    <span>WhatsApp</span>
                  </div>
                </SelectItem>
                <SelectItem key="telegram" value="telegram" textValue="Telegram">
                  <div className="flex items-center gap-2">
                    {renderIcon('telegram')}
                    <span>Telegram</span>
                  </div>
                </SelectItem>
                <SelectItem key="spotify" value="spotify" textValue="Spotify">
                  <div className="flex items-center gap-2">
                    {renderIcon('spotify')}
                    <span>Spotify</span>
                  </div>
                </SelectItem>
              </Select>
              
              <div className="p-4 bg-gray-100 rounded-lg">
                <h4 className="font-medium mb-2">Vista previa:</h4>
                <div className="flex items-center gap-2 text-sm">
                  {renderIcon(tabActual.icono, 'text-blue-500')}
                  <span>{tabActual.nombre || 'Nombre del Tab'}</span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="danger" 
              variant="light" 
              onPress={() => {
                setErrorTabModal('') // Limpiar error al cancelar
                onTabModalClose()
              }}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onPress={guardarTab}
              isLoading={guardandoCambios}
              disabled={!tabActual.nombre.trim()}
            >
              {editandoTabIndex === -1 ? 'Crear Tab' : 'Guardar Cambios'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default PerfilPublico 